import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  ElementRef,
  TemplateRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TNil } from '@app/app.interface';
import { ViewportService } from '@core/services/viewport.service';
import { EAnalyticsStatus, EMatchingType } from '@enums/campaign-analytics.enum';
import { EDeliverableType, EPlatformFeature } from '@enums/campaign.enum';
import { IManualDeliverableStatus } from '@interfaces/campaign-analytics-api.interface';
import { IAnalyticsNode, ICtaResult } from '@interfaces/campaign-analytics.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  EDetailLevel,
  ICampaignExportApi,
} from '@pages/campaigns/campaign-details/campaign-details-banner/campaign-export-dialog/campaign-export-dialog.interface';
import { DatatableComponent } from '@shared/components/datatable/datatable.component';
import {
  EHeaderClasses,
  ICustomColumn,
  IDatatableConfiguration,
  TDatatableColumn,
} from '@shared/components/datatable/datatable.interface';
import { IconComponent } from '@shared/components/icon/icon.component';
import { StatusBadgeComponent } from '@shared/components/status-badge/status-badge.component';
import { TStatusBadge } from '@shared/components/status-badge/status-badge.interface';
import { TextClampTooltipDirective } from '@shared/directives/text-clamp-tooltip.directive';
import { DeliverableUtilitiesService } from '@utilities/deliverable-utilities.service';
import { DurationUtilitiesService } from '@utilities/duration-utilities.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ICtaAnalyticsData } from '../cta-analytics-table.interface';
import { IDeliverableCard } from './cta-analytics-details-deliverable-card/cta-analytics-details-deliverable-card.interface';
import { ManualDeliverableStatusMapStore } from './cta-analytics-details-deliverable-card/manual-deliverable-status/manual-deliverable-status-map.store';
import { CtaAnalyticsDetailsDeliverablesHeaderComponent } from './cta-analytics-details-deliverables-header/cta-analytics-details-deliverables-header.component';
import { CtaAnalyticsDetailsKpiGoalsTableComponent } from './cta-analytics-details-kpi-goals-table/cta-analytics-details-kpi-goals-table.component';
import { IKpiCard } from './cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';
import { CtaAnalyticsDetailsPlannedViewComponent } from './cta-analytics-details-planned-view/cta-analytics-details-planned-view.component';
import {
  ICtaAnalyticsDetailsTableRow,
  IDeliverableSelectedEvent,
  TTableAnalyticsEntry,
} from './cta-analytics-details.interface';
import { CtaAnalyticsDetailsService } from './cta-analytics-details.service';
import { CtaAnalyticsDetailsStore } from './cta-analytics-details.store';

@Component({
  imports: [
    NgTemplateOutlet,
    NgClass,
    CtaAnalyticsDetailsDeliverablesHeaderComponent,
    CtaAnalyticsDetailsKpiGoalsTableComponent,
    CtaAnalyticsDetailsPlannedViewComponent,
    DatatableComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    NgxSkeletonLoaderModule,
    ReactiveFormsModule,
    StatusBadgeComponent,
    TextClampTooltipDirective,
    TranslateModule,
    IconComponent,
  ],
  selector: 'app-cta-analytics-details',
  styleUrls: ['./cta-analytics-details.component.scss'],
  templateUrl: './cta-analytics-details.component.html',
})
export class CtaAnalyticsDetailsComponent {
  public readonly analyticsNode = input.required<IAnalyticsNode>();
  public readonly creatorId = input.required<string>();
  public readonly ctaResult = input.required<ICtaResult>();
  public readonly deliverableCardData = input.required<IDeliverableCard[]>();
  public readonly execution = input.required<ICtaAnalyticsData>();
  public readonly kpiGoalsKpiCards = input.required<IKpiCard[]>();
  public readonly platformFeature = input.required<EPlatformFeature | null>();

  public readonly adminMode = input<boolean>(false);
  public readonly exportMode = input<boolean>(false);
  public readonly campaignExport = input<ICampaignExportApi | null>(null);
  public readonly creatorBriefingMode = input<boolean>(false);

  public readonly ctaExecutionChange = output<string>();
  public readonly deliverableSelected = output<IDeliverableSelectedEvent>();

  public readonly selectedDeliverableUniqueId = CtaAnalyticsDetailsService.selectedDeliverableUniqueId;
  public readonly mobileBriefing = computed(() => this.creatorBriefingMode() && this.viewportService.isMobile());
  public readonly EMatchingType = EMatchingType;

  public readonly isAnalyticsPlannedMode = computed(
    () =>
      this.analyticsNode().executionStatus === EMatchingType.planned ||
      this.analyticsNode().executionStatus === EMatchingType.missing,
  );

  public readonly datatableConfig = computed<IDatatableConfiguration<ICtaAnalyticsDetailsTableRow>>(() => ({
    defaultSort: { active: '', direction: '' },
    isClientSide: true,
    isLoaderEnabled: false,
    isPaginationEnabled: false,
    isRowClickEnabled: this.canClickOnDeliverable(),
    isSearchEnabled: false,
    showNoDataText: true,
    skipInitialRequest: true,
    tableMargin: '0 0 -12px -24px',
    tablePadding: '0 16px 24px 48px',
  }));

  public readonly deliverablesTableColumns: TDatatableColumn<ICtaAnalyticsDetailsTableRow, string>[] = [
    {
      dataViewStrategy: (row) => row.statusSortValue,
      headerClasses: [EHeaderClasses.minWidth100],
      id: 'status',
      isSortable: true,
    },
    {
      dataViewStrategy: (row) => row.deliverable.toLowerCase(),
      headerClasses: [EHeaderClasses.minWidth150],
      id: 'deliverable',
      isSortable: true,
    },
    {
      dataViewStrategy: (row) => row.findingsSortValue,
      headerClasses: [EHeaderClasses.minWidth100],
      id: 'findings',
      isSortable: true,
    },
  ];

  public readonly observationsTableColumns: TDatatableColumn<ICtaAnalyticsDetailsTableRow, string>[] = [
    {
      dataViewStrategy: (row) => row.statusSortValue,
      headerClasses: [EHeaderClasses.minWidth100],
      id: 'status',
      isSortable: true,
    },
    {
      dataViewStrategy: (row) => row.tracking.toLowerCase(),
      headerClasses: [EHeaderClasses.minWidth150],
      id: 'tracking',
      isSortable: true,
    },
    {
      dataViewStrategy: (row) => row.findingsSortValue,
      headerClasses: [EHeaderClasses.minWidth100],
      id: 'findings',
      isSortable: true,
    },
  ];

  public readonly deliverablesVisibleColumns = computed<TDatatableColumn<ICtaAnalyticsDetailsTableRow, string>[]>(
    () => {
      return this.canShowStatus()
        ? this.deliverablesTableColumns
        : this.deliverablesTableColumns.filter((column) => column.id !== 'status');
    },
  );

  public readonly observationsVisibleColumns = computed<TDatatableColumn<ICtaAnalyticsDetailsTableRow, string>[]>(
    () => {
      return this.canShowStatus()
        ? this.observationsTableColumns
        : this.observationsTableColumns.filter((column) => column.id !== 'status');
    },
  );

  public readonly deliverablesCustomColumns = computed<ICustomColumn[]>(() => {
    const columns: ICustomColumn[] = [
      { isSortable: true, name: 'deliverable', template: this.deliverableColumnTemplate() },
      { isSortable: true, name: 'findings', template: this.findingsColumnTemplate() },
    ];

    if (this.canShowStatus()) {
      columns.unshift({ isSortable: true, name: 'status', template: this.statusColumnTemplate() });
    }

    return columns;
  });

  public readonly observationsCustomColumns = computed<ICustomColumn[]>(() => {
    const columns: ICustomColumn[] = [
      { isSortable: true, name: 'tracking', template: this.trackingColumnTemplate() },
      { isSortable: true, name: 'findings', template: this.findingsColumnTemplate() },
    ];

    if (this.canShowStatus()) {
      columns.unshift({ isSortable: true, name: 'status', template: this.statusColumnTemplate() });
    }

    return columns;
  });

  public readonly manualDeliverableStatuses = computed<
    Map<string, IManualDeliverableStatus | null | undefined> | undefined
  >(() => {
    if (!this.deliverableCardData().length) {
      return undefined;
    }

    if (!this.canFetchManualStatuses()) {
      return new Map<string, IManualDeliverableStatus | null | undefined>(
        this.deliverableCardData().map((deliverable) => [`${this.creatorId()}_${deliverable.id}`, null]),
      );
    }

    if (!this.store.manualDeliverableStatuses$()) {
      return new Map<string, IManualDeliverableStatus | null | undefined>(
        this.deliverableCardData().map((deliverable) => [`${this.creatorId()}_${deliverable.id}`, undefined]),
      );
    }

    return new Map<string, IManualDeliverableStatus | null | undefined>(
      this.deliverableCardData().map((deliverable) => {
        const manualDeliverableStatus = this.store.manualDeliverableStatuses$()?.get(deliverable.id) ?? null;
        // When no API record exists, fall back to statusManual from card data (persisted DB value)
        const statusFromApi = manualDeliverableStatus !== null ? manualDeliverableStatus.status : undefined;
        this.temporaryValidStatusMap.set(
          `${this.creatorId()}_${deliverable.id}`,
          statusFromApi !== undefined ? statusFromApi : (deliverable.statusManual ?? null),
        );

        return [`${this.creatorId()}_${deliverable.id}`, manualDeliverableStatus ?? null];
      }),
    );
  });

  public readonly canFetchManualStatuses = computed(() => {
    return (
      !this.creatorBriefingMode() &&
      (!this.exportMode() || this.campaignExport()?.detailLevel === EDetailLevel.detailedAnalytics) &&
      !this.adminMode()
    );
  });

  public readonly canClickOnDeliverable = computed(() => {
    return (
      !this.exportMode() ||
      (this.campaignExport()?.showDeliverableStatus &&
        this.campaignExport()?.detailLevel === EDetailLevel.detailedAnalytics)
    );
  });

  public readonly canShowStatus = computed(() => {
    return (
      !this.exportMode() ||
      (this.campaignExport()?.showDeliverableStatus && this.campaignExport()?.detailLevel !== EDetailLevel.generalInfo)
    );
  });

  public readonly showSearch = signal<boolean>(false);
  public readonly searchControl = new FormControl<string>('');
  public readonly searchValue = signal<string>('');

  public readonly searchFilteredDeliverables = computed<IDeliverableCard[]>(() => {
    const searchValue = this.searchValue().toLowerCase().trim();

    if (!searchValue) {
      return this.deliverableCardData();
    }

    return this.deliverableCardData().filter((deliverable) => deliverable.name.toLowerCase().includes(searchValue));
  });

  public readonly searchFilteredKpiGoalsKpiCards = computed<IKpiCard[]>(() => {
    const searchValue = this.searchValue().toLowerCase().trim();
    const cards = searchValue
      ? this.kpiGoalsKpiCards().filter((kpiCard) => (kpiCard.title ?? '').toLowerCase().includes(searchValue))
      : this.kpiGoalsKpiCards();

    return [...cards].sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''));
  });

  public readonly tableRows = computed<ICtaAnalyticsDetailsTableRow[]>(() => {
    this.manualDeliverableStatusMapStore.updateVersion$();

    return this.searchFilteredDeliverables().map((deliverable) => this.createTableRow(deliverable));
  });

  public readonly deliverablesTableRows = computed<ICtaAnalyticsDetailsTableRow[]>(() => {
    return this.tableRows()
      .filter((row) => !row.isTracking)
      .sort((a, b) => a.deliverable.localeCompare(b.deliverable));
  });

  public readonly observationsTableRows = computed<ICtaAnalyticsDetailsTableRow[]>(() => {
    return this.tableRows()
      .filter((row) => row.isTracking)
      .sort((a, b) => a.tracking.localeCompare(b.tracking));
  });

  public readonly store = inject(CtaAnalyticsDetailsStore);
  public readonly manualDeliverableStatusMapStore = inject(ManualDeliverableStatusMapStore);
  public readonly deliverableUtilitiesService = inject(DeliverableUtilitiesService);
  private readonly durationUtilitiesService = inject(DurationUtilitiesService);
  private readonly translate = inject(TranslateService);
  private readonly viewportService = inject(ViewportService);

  private readonly deliverableColumnTemplate = viewChild.required<TemplateRef<unknown>>('deliverableColumn');
  private readonly findingsColumnTemplate = viewChild.required<TemplateRef<unknown>>('findingsColumn');
  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  private readonly statusColumnTemplate = viewChild.required<TemplateRef<unknown>>('statusColumn');
  private readonly trackingColumnTemplate = viewChild.required<TemplateRef<unknown>>('trackingColumn');

  private selectedDeliverableId: string | null = null;
  private previousSelectedDeliverableId: string | null = null;
  private temporaryValidStatusMap = new Map<string, EAnalyticsStatus | TNil>();

  constructor() {
    this.createManualDeliverableStatusMapEffect();

    effect(() => {
      if (!this.canFetchManualStatuses()) {
        return;
      }

      this.store.fetchManualDeliverableStatuses({
        campaignExportId: this.exportMode() ? (this.campaignExport()?.id ?? null) : null,
        campaignId: this.execution().campaignId,
        creatorId: this.creatorId(),
        ctaTemplateId: this.execution().ctaTemplateId,
        socialContentId: this.execution().socialContentId,
      });
    });

    this.searchControl.valueChanges.subscribe((value) => {
      this.searchValue.set(value ?? '');
    });
  }

  public onSearchClick(): void {
    this.showSearch.set(true);

    setTimeout(() => {
      this.searchInput()?.nativeElement?.focus();
    });
  }

  public onSearchBlur(): void {
    if (!this.searchControl.value?.trim()) {
      this.showSearch.set(false);
      this.searchControl.setValue('');
      this.searchValue.set('');
    }
  }

  public clearSearch(): void {
    this.searchControl.setValue('');
    this.searchValue.set('');
  }

  public onRowClick(row: ICtaAnalyticsDetailsTableRow): void {
    this.onDeliverableSelect(row.data);
  }

  public onDeliverableSelect(deliverable: IDeliverableCard): void {
    if (!this.canClickOnDeliverable()) {
      return;
    }

    if (
      this.previousSelectedDeliverableId !== deliverable.id ||
      CtaAnalyticsDetailsService.previousSelectedDeliverableUniqueId() !== deliverable.uniqueId
    ) {
      CtaAnalyticsDetailsService.selectedDeliverableUniqueId.set(deliverable.uniqueId);
      CtaAnalyticsDetailsService.previousSelectedDeliverableUniqueId.set(deliverable.uniqueId);
      this.selectedDeliverableId = deliverable.id;
      this.previousSelectedDeliverableId = deliverable.id;
    } else {
      CtaAnalyticsDetailsService.selectedDeliverableUniqueId.set(null);
      CtaAnalyticsDetailsService.previousSelectedDeliverableUniqueId.set(null);
      this.selectedDeliverableId = null;
      this.previousSelectedDeliverableId = null;
    }

    this.deliverableSelected.emit({
      deliverableId: this.selectedDeliverableId,
      deliverableType: deliverable.type,
      executionAnalytics: this.execution(),
    });
  }

  private createTableRow(deliverable: IDeliverableCard): ICtaAnalyticsDetailsTableRow {
    const isExecutionPlanned = this.execution().status === EMatchingType.planned;
    const isTracking = this.deliverableUtilitiesService.isTrackingDeliverable(deliverable.type);
    const status = this.getResolvedStatus(deliverable);

    let rowStatus: TStatusBadge = status;
    let statusSortValue: number;

    if (isExecutionPlanned) {
      rowStatus = EMatchingType.planned;
      statusSortValue = 5;
    } else {
      if (isTracking && status === EAnalyticsStatus.ok) {
        rowStatus = 'findings';
      } else if (isTracking && status === EAnalyticsStatus.failed) {
        rowStatus = 'noFindings';
      } else {
        rowStatus = status;
      }

      statusSortValue = CtaAnalyticsDetailsService.STATUS_SORT_VALUE_MAP[status];
    }

    return {
      data: deliverable,
      deliverable: deliverable.name,
      findings: isTracking
        ? this.getObservationFindingsDisplay(deliverable)
        : this.getDeliverableFindingsDisplay(deliverable),
      findingsSortValue: isTracking
        ? this.getObservationFindingsSortValue(deliverable)
        : this.getDeliverableFindingsSortValue(deliverable),
      id: deliverable.id,
      isStatusLoading: isExecutionPlanned ? false : this.isStatusLoading(deliverable),
      isTracking,
      status: rowStatus,
      statusSortValue,
      tracking: deliverable.name,
      uniqueId: deliverable.uniqueId,
    };
  }

  private getResolvedStatus(deliverable: IDeliverableCard): EAnalyticsStatus {
    const key = `${this.creatorId()}_${deliverable.id}`;
    const updatedStatusManual = this.manualDeliverableStatusMapStore.getStatus(key);

    if (updatedStatusManual !== undefined) {
      return updatedStatusManual === null ? deliverable.status : updatedStatusManual;
    }

    return this.manualDeliverableStatuses()?.get(key)?.status ?? deliverable.statusManual ?? deliverable.status;
  }

  private isStatusLoading(deliverable: IDeliverableCard): boolean {
    if (this.creatorBriefingMode() || !this.canShowStatus()) {
      return false;
    }

    return this.manualDeliverableStatuses()?.get(`${this.creatorId()}_${deliverable.id}`) === undefined;
  }

  private getObservationFindingsDisplay(deliverable: IDeliverableCard): number {
    return this.getKeywordFindingsCount(this.getAnalyticsItem(deliverable));
  }

  private getObservationFindingsSortValue(deliverable: IDeliverableCard): number {
    return this.getKeywordFindingsCount(this.getAnalyticsItem(deliverable));
  }

  private getDeliverableFindingsDisplay(deliverable: IDeliverableCard): number | string {
    const analyticsItem = this.getAnalyticsItem(deliverable);

    switch (deliverable.type) {
      case EDeliverableType.chatBot:
      case EDeliverableType.chatbotMulti:
      case EDeliverableType.talkingPointAi:
      case EDeliverableType.productPlacement:
        return analyticsItem?.findingsCount ?? analyticsItem?.findings?.length ?? 0;
      case EDeliverableType.gamePlay:
        return this.durationUtilitiesService.getDuration(analyticsItem?.totalPlayTime ?? 0, 'minutes', true);
      default:
        if (CtaAnalyticsDetailsService.IMAGE_FINDING_DELIVERABLE_TYPES.includes(deliverable.type)) {
          return this.translate.instant(this.hasDirectFindings(analyticsItem) ? 'shared.yes' : 'shared.no');
        }

        return this.getKeywordFindingsCount(analyticsItem);
    }
  }

  private getDeliverableFindingsSortValue(deliverable: IDeliverableCard): number {
    const analyticsItem = this.getAnalyticsItem(deliverable);

    switch (deliverable.type) {
      case EDeliverableType.chatBot:
      case EDeliverableType.chatbotMulti:
      case EDeliverableType.talkingPointAi:
      case EDeliverableType.productPlacement:
        return analyticsItem?.findingsCount ?? analyticsItem?.findings?.length ?? 0;
      case EDeliverableType.gamePlay:
        return analyticsItem?.totalPlayTime ?? 0;
      default:
        if (CtaAnalyticsDetailsService.IMAGE_FINDING_DELIVERABLE_TYPES.includes(deliverable.type)) {
          return this.hasDirectFindings(analyticsItem) ? 1 : 0;
        }

        return this.getKeywordFindingsCount(analyticsItem);
    }
  }

  private getKeywordFindingsCount(analyticsItem: TTableAnalyticsEntry | undefined): number {
    return analyticsItem?.keywords?.reduce((total, keyword) => total + (keyword.findingsCount ?? 0), 0) ?? 0;
  }

  private hasDirectFindings(analyticsItem: TTableAnalyticsEntry | undefined): boolean {
    return (analyticsItem?.findingsCount ?? analyticsItem?.findings?.length ?? 0) > 0;
  }

  private getAnalyticsItem(deliverable: IDeliverableCard): TTableAnalyticsEntry | undefined {
    const { ctaAnalytics } = this.execution();

    if (!ctaAnalytics) {
      return undefined;
    }

    const collectionKey = CtaAnalyticsDetailsService.DELIVERABLE_ANALYTICS_COLLECTION_MAP[deliverable.type];
    const collection = ctaAnalytics[collectionKey] as TTableAnalyticsEntry[] | undefined;

    return collection?.find((item) => item.deliverableId === deliverable.id);
  }

  private createManualDeliverableStatusMapEffect(): void {
    effect(() => {
      const manualDeliverableStatuses = this.manualDeliverableStatuses();

      if (!this.deliverableCardData().length || !manualDeliverableStatuses) {
        this.manualDeliverableStatusMapStore.initializeMap(new Map());

        return;
      }

      if (!this.canFetchManualStatuses()) {
        this.manualDeliverableStatusMapStore.initializeMap(
          manualDeliverableStatuses as Map<string, EAnalyticsStatus | TNil>,
        );

        return;
      }

      // Still loading — preserve map store to avoid wiping in-session changes
      if (!this.store.manualDeliverableStatuses$()) {
        return;
      }

      this.manualDeliverableStatusMapStore.initializeMap(this.temporaryValidStatusMap);
    });
  }
}
